import * as R from 'ramda';

import { findBoundsOfCoordinates } from '@utils/bounds.ts';

import type { PluginConfig } from '../config-schema.ts';

type AuthConfig = PluginConfig['auth'];

const fetchURL = async (token, url) => {
  if (token) {
    return fetch(url, {
      headers: {
        Authorization: token,
      },
    });
  } else {
    return fetch(url);
  }
};

export const createFetchJson = (token?: string) => (url: string) =>
  fetchURL(token, url).then(response => response.json());
export const createFetchText = (token?: string) => (url: string) =>
  fetchURL(token, url).then(response => response.text());

export const createFetchManifestJson =
  (auth?: AuthConfig, onManifestForbidden?: (status: number) => unknown) => async url => {
    const response = await fetch(url);
    if (auth?.type === 'kiosk' && response.status === 403) {
      await onManifestForbidden?.(response.status);
      throw new Error(
        `Kiosk signed manifest request was forbidden (${response.status}); the signed URL may have expired.`,
      );
    }
    return response.json();
  };

// If category is undefined, style service returns the default airport style doc
export const fetchStyleDoc = (venueId: string, venueCategory?: string) => {
  const styleUrl = `https://style.maps.atrius.com/v2/style?venueId=${venueId}&category=${venueCategory}`;
  return fetchURL(null, styleUrl)
    .then(response => response.json())
    .then(body => body.data);
};

const baseContentUrl = contentStage => `https://api.content.locuslabs.com/${contentStage}`;

const remapContentUrl = (url, category, contentStage, accountId, venueId, key) => {
  if (isSignedUrl(url)) {
    return url;
  }
  if (key === 'theme' || key === 'style') {
    return `${baseContentUrl(contentStage)}/${category}/${key}/${venueId}/${accountId}/${key}.json`;
  }
  return url.replace(/https:\/\/content.locuslabs.com/gi, baseContentUrl(contentStage));
};

const isSignedUrl = url => {
  if (typeof url !== 'string') {
    return false;
  }
  try {
    const searchParams = new URL(url).searchParams;
    return ['Expires', 'Signature', 'Key-Pair-Id'].every(name => searchParams.has(name));
  } catch {
    return false;
  }
};

const remapEachURLForContentStage = (category, files, contentStage, accountId, venueId) =>
  R.mapObjIndexed((file, key) => remapContentUrl(file, category, contentStage, accountId, venueId, key), files);

const getContentStage = vconfig => {
  const stage = vconfig.deepLinkProps ? vconfig.deepLinkProps.contentStage : null;
  return stage === 'alpha' || stage === 'beta' || stage === 'prod' ? stage : null;
};

// Groups of locale tags that refer to the same language variant and should match each other
// when no exact match is available. zh-CN/zh-SG use simplified script; zh-TW/zh-HK/zh-MO use traditional.
const LOCALE_ALIAS_GROUPS = [
  ['zh-CN', 'zh-SG', 'zh-Hans'],
  ['zh-TW', 'zh-HK', 'zh-MO', 'zh-Hant'],
];

const getLocaleAliases = locale => {
  const group = LOCALE_ALIAS_GROUPS.find(g => g.includes(locale));
  return group ? group.filter(l => l !== locale) : [];
};

// Three tiers of locale match, from most to least specific. Returned as predicates
// so the resolver below can walk all venues at each tier before falling to the next.
//   1. Exact full-tag match (e.g. "zh-Hans" === "zh-Hans")
//   2. Known aliases (e.g. browser "zh-CN" matches venue "zh-Hans")
//   3. Two-char language-subtag prefix on both sides (e.g. "fr-FR" vs "fr_CA" both reduce to "fr")
const getLocaleMatchTiers = browserLang => {
  const aliases = getLocaleAliases(browserLang);
  const prefix = browserLang.slice(0, 2);
  return [
    locale => locale === browserLang,
    locale => aliases.includes(locale),
    locale => locale.slice(0, 2) === prefix,
  ];
};

// Resolve a browser locale to a venue. Within each tier, prefer `currentVenue` over
// other siblings when both would match — this keeps the caller on their intended venue
// when it satisfies the preference, and prevents a lower-tier match (e.g. "en" prefix)
// from yanking a user off an exactly-matching region sibling.
export const findVenueForLocale = (venues, browserLang, currentVenue = null) => {
  for (const matches of getLocaleMatchTiers(browserLang)) {
    if (currentVenue && matches(currentVenue.locale)) {
      return currentVenue;
    }
    const venue = venues.find(v => matches(v.locale));
    if (venue) {
      return venue;
    }
  }
  return null;
};

export const getVenueDataFromUrls = async (vconfig, fetchJson, languagesToTry, fetchManifestJson = fetchJson) => {
  const stages = {
    alpha: 'alpha-a.locuslabs.com',
    beta: 'beta-a.locuslabs.com',
    gamma: 'gamma-a.locuslabs.com',
    prod: 'a.locuslabs.com',
  };

  const { assetStage, accountId, formatVersion } = vconfig;
  let { venueId } = vconfig;
  const stageUrl = stages[assetStage] || stages.prod;
  const accountUrl = `https://${stageUrl}/accounts/${accountId}`;
  const assetFormat = formatVersion || 'v5';
  const usesKioskAuth = vconfig.auth?.type === 'kiosk';
  if (usesKioskAuth && (typeof vconfig.auth.signedManifestUrl !== 'string' || !vconfig.auth.signedManifestUrl.trim())) {
    throw new Error('Kiosk auth requires a non-empty signedManifestUrl');
  }

  // Load the v5.json resource
  const venueList =
    !usesKioskAuth && vconfig.dataFetch && globalThis[vconfig.dataFetch] && globalThis[vconfig.dataFetch].getFiles
      ? await globalThis[vconfig.dataFetch].getFiles(vconfig)
      : await fetchManifestJson(usesKioskAuth ? vconfig.auth.signedManifestUrl : `${accountUrl}/${assetFormat}.json`);

  // Reassign venue id to venueId+language preference if that venue in that language is available to us in the venue list.
  // Only attempt language resolution when the venue has a venueCode (indicating language siblings may exist).
  // Venues without a venueCode (e.g. custom tiled maps provided via dataFetch) skip resolution entirely.
  const venueCode = venueList[venueId] ? venueList[venueId].venueCode : null;

  if (venueCode) {
    const venuesWithSameVenueCode = Object.values(venueList).filter(v => v.venueCode === venueCode);
    const currentVenue = venueList[venueId];
    for (const browserLang of languagesToTry) {
      const venue = findVenueForLocale(venuesWithSameVenueCode, browserLang, currentVenue);
      if (venue) {
        venueId = venue.id;
        vconfig.venueId = `${venueId}`;
        break;
      }
    }
  }

  if (!venueList[venueId]) {
    throw Error(`Attempt to access venue ${venueId} which is not within venue list: ${Object.keys(venueList)}`);
  }
  const files = venueList[venueId].files; // mapping of asset "types" (spritesheet, style, badges, etc) to their URL
  const fetchedData =
    !usesKioskAuth && vconfig.dataFetch && globalThis[vconfig.dataFetch] && globalThis[vconfig.dataFetch].getVenueData
      ? await globalThis[vconfig.dataFetch].getVenueData(vconfig)
      : await fetchJson(files.venueData);

  const venueData = fetchedData[venueId];

  // For tilemaps, we need to embellish the venue data with structure
  if (venueData.tileServerAuthInfo) {
    embellishTilemapVenueData(venueData);
  }

  venueData.venueList = venueList;
  const contentStage = getContentStage(vconfig);
  venueData.files = contentStage // if a contentStage is defined, remap content URLs (see docs/Content-And-Asset-Loading/contentStaging.md)
    ? remapEachURLForContentStage(venueData.category, files, contentStage, accountId, venueId)
    : files;

  return venueData;
};

export const buildStructures = venueData => {
  const { structureOrder, structures } = venueData;
  return structureOrder.map(structureId => {
    const structure = structures[structureId];
    // Was going to use this for selecting floors - but it seems floor bounds are incorrect? (was testing LAX/lax-msc-2 for example)
    Object.values(structure.levels).forEach(level => (level.bounds = findBoundsOfCoordinates(level.boundsPolygon)));
    const bounds = findBoundsOfCoordinates(structure.boundsPolygon);
    return { ...structure, bounds };
  });
};

function embellishTilemapVenueData(venueData) {
  const additionalFields = {
    defaultOrdinal: 0, // this and below override the venue data
    defaultStructureId: 'singleBuilding',
    formatVersion: 'v5',
    // Note: this structures object does not represent the "buildingsAndLevels" as
    // managed within this app. This is a mock structure for the purposes of rendering
    // a tiled map. It is always a single building, single floor. If user switches
    // buildings or floors, it unmounts this map and remounts a new map with that building/floor
    structures: {
      singleBuilding: {
        name: 'singleBuilding',
        boundsPolygon: [],
        defaultLevelId: 'singleLevel',
        id: 'singleBuilding',
        levels: {
          singleLevel: {
            boundsPolygon: [],
            clfloor: 0,
            details: '',
            id: 'singleLevel',
            name: 'singleLevel',
            ordinal: 0,
          },
        },
      },
    },
    structureOrder: ['singleBuilding'],
  };

  for (const key in additionalFields) {
    venueData[key] = additionalFields[key];
  }
}

const between = (val, lim1, lim2) => val >= Math.min(lim1, lim2) && val <= Math.max(lim1, lim2);

// exchanges the first and second items in an array, preserving the rest of the array untouched
// i.e. [1,2,3,4,5] => [2,1,3,4,5]
const swapFirstTwoItems = ([c1, c2, ...c3]) => [c2, c1, ...c3];

/**
 * Since coordinates are often expressed as [lat, lng] but required by GeoJSON to be [lng, lat],
 * this function will take a list of coordinate pairs and ensure they are in the required format
 * by comparing to the venue bounds.
 *
 * @param  {array.array.float} coords list of coordinate pairs, such as [[36.3,-115.85], [36.5, -115.34]]
 * @param  {object} venueBounds contains props {ne, sw} which each are objects containing {lat,lng} properties
 * @returns {array.array.float} with [lng,lat,...] for each item
 */
export const normalizeCoords = (coords, venueBounds) => {
  if (!coords || !Array.isArray(coords) || coords.length < 1) {
    return coords;
  }

  if (between(coords[0][0], venueBounds.ne.lng, venueBounds.sw.lng)) {
    // looks like a lat - so already in proper order
    return coords;
  }

  // wrong order, so swtich em
  return coords.map(swapFirstTwoItems);
};

// If config.useDynamicUrlParams is true, allow changing even security-related parameters in
// the URL - else, only allow the changing of "end-user-changable" parameters, such as vid
// export function updateConfigWithUrlParams (config) {
//   const dlp = config.deepLinkProps
//   if (dlp) {
//     const venueId = dlp.vid ? dlp.vid : config.venueId
//     const assetStage = config.useDynamicUrlParams && dlp.stage ? dlp.stage : config.assetStage
//     const accountId = assetStage === 'alpha' ? 'A1VPTJKREFJWX5' : config.accountId
//     return { ...config, venueId, assetStage, accountId }
//   }
//   return config
// }
